import { createCounter, createHistogram, createObservableGauge } from './uiMetrics';
// namespace import: forceSampleTraces ships in the trace-identity PR; degrade gracefully until it lands
import * as uiOtel from './uiOtel';

// Chokepoint metrics: permanent detectors so a future unpaginated fetch, oversized table,
// or slow typeahead reports itself instead of waiting for a customer escalation.

export const COLLECTION_ROWS_FORCE_THRESHOLD = 5000;
export const KEYSTROKE_FORCE_THRESHOLD_MS = 500;
const SELECT_FILTER_SAMPLE_RATE = 0.05;
// below this resident-model count a collection isn't interesting for the heap-ratchet gauge,
// and emitting every tiny collection would waste series
const STORE_INVENTORY_MIN_MODELS = 100;
const POST_BOOT_INVENTORY_DELAY_MS = 60000;
// a polling view can request a new URL on every refresh; past this many distinct URLs in one
// route view, duplicate tracking restarts so the map stays bounded
const DUPLICATE_TRACKING_MAX_URLS = 2000;

export function maybeForceSampleTraces(reason) {
  uiOtel.forceSampleTraces?.(reason);
}

// path segments that are values rather than route names; static segments never look like these
const ID_SEGMENT_PATTERNS = [
  /^\d+$/, // numeric ids
  /^[0-9a-f-]{16,}$/i, // uuids, long hex hashes
  /^\d{4}-\d{2}-\d{2}/, // dates
  /[.:%@,]/, // IPv4/IPv6 addresses, hostnames, file names, percent-encoded values, emails, id lists
  /^(?=.*\d).{20,}$/ // long tokens containing a digit (hashes, object keys)
];

// API endpoints whose fourth segment is a free-form name (MIB module), which no shape heuristic can
// tell apart from a static action. Deeper names are already covered by the depth cap.
const NAME_PARAM_ENDPOINTS = ['/api/ui/mibs/'];

// app routes nest free-form params (device names, shared-link hashes, tabs) below the first three
// segments, e.g. /v4/infrastructure/devices/:deviceName; deeper segments collapse to *
const ROUTE_LABEL_DEPTH = 3;
// API labels keep /api/ui/<resource>/<action>. Free-form segments below that (shared-link slugs,
// device or tag names) collapse to *, so they never reach the shared metrics store.
const API_LABEL_DEPTH = 4;

function normalizeSegments(pathname) {
  return pathname
    .split('/')
    .map((segment) => (ID_SEGMENT_PATTERNS.some((pattern) => pattern.test(segment)) ? ':id' : segment));
}

// keeps the first `depth` normalized segments, and /* when the path goes deeper
function toCappedLabel(pathname, depth) {
  // leading '' from the root slash plus the kept segments
  const segments = normalizeSegments(pathname).slice(0, depth + 2);
  if (segments.length > depth + 1) {
    segments[depth + 1] = '*';
  }
  return segments.join('/') || '/';
}

/**
 * Normalizes an app pathname into a low-cardinality label: the first three segments, with id-shaped
 * ones as :id, and /* when the path goes deeper.
 */
export function getRouteLabel(pathname = window.location.pathname) {
  return toCappedLabel(pathname, ROUTE_LABEL_DEPTH);
}

/**
 * Normalizes an /api/ pathname into a low-cardinality label: /api/ui/<resource>/<action>, with
 * id-shaped segments as :id, and /* when the path goes deeper.
 */
export function getApiEndpointLabel(pathname) {
  const namePrefix = NAME_PARAM_ENDPOINTS.find((prefix) => pathname.startsWith(prefix));
  if (namePrefix) {
    return `${namePrefix}:name`;
  }
  return toCappedLabel(pathname, API_LABEL_DEPTH);
}

export function getEndpointLabel(url) {
  try {
    return getApiEndpointLabel(new URL(url, window.location.origin).pathname);
  } catch (e) {
    return 'invalid';
  }
}

export function getCountBucket(count) {
  if (!Number.isFinite(count) || count < 0) {
    return 'unknown';
  }
  if (count < 100) {
    return 'lt100';
  }
  if (count < 1000) {
    return 'lt1k';
  }
  if (count < 10000) {
    return 'lt10k';
  }
  return 'gte10k';
}

// --- instruments (module scope is safe: they bind lazily once initializeMetrics runs) ---

const collectionRowsTotal = createCounter({
  name: 'collection_rows_total',
  description: 'Rows deserialized into collections, by collection class and route'
});
const collectionProcessSeconds = createHistogram({
  name: 'collection_process_duration_seconds',
  description: 'Collection.processData deserialize + model-build duration'
});
const tableRowsRenderedTotal = createCounter({
  name: 'table_rows_rendered_total',
  description: 'Rows handed to table body renders, by component and virtualization'
});
const selectFilterSeconds = createHistogram({
  name: 'select_filter_duration_seconds',
  description: 'SelectPopover option filtering duration per keystroke (sampled)'
});
// decoded-bytes and duplicate-fetch counters are on the company_id allowlist;
// the endpoint-dimensioned TTFB histogram is NOT (histograms never carry company_id)
const apiDecodedBytesTotal = createCounter({
  name: 'api_resource_decoded_bytes_total',
  description: 'Decoded (post-gzip) bytes fetched from /api/ endpoints',
  includeCid: true
});
const apiEncodedBytesTotal = createCounter({
  name: 'api_resource_encoded_bytes_total',
  description: 'Encoded (wire) bytes fetched from /api/ endpoints'
});
const apiTtfbSeconds = createHistogram({
  name: 'api_resource_ttfb_seconds',
  description: 'Time to first byte for /api/ requests'
});
const duplicateFetchTotal = createCounter({
  name: 'duplicate_fetch_total',
  description: 'Repeat fetches of an identical URL within one route view',
  includeCid: true
});

export function recordCollectionProcessed({ collection, rows, durationMs }) {
  const labels = { collection, route: getRouteLabel() };
  collectionRowsTotal(labels, rows);
  collectionProcessSeconds.record(durationMs / 1000, labels);
  if (rows > COLLECTION_ROWS_FORCE_THRESHOLD) {
    maybeForceSampleTraces('oversized_collection');
  }
}

export function recordTableRowsRendered({ component, virtualized, rows }) {
  tableRowsRenderedTotal({ component, virtualized: !!virtualized }, rows);
}

export function recordSelectFilter({ durationMs, optionsCount }) {
  const slow = durationMs > KEYSTROKE_FORCE_THRESHOLD_MS;
  if (slow) {
    maybeForceSampleTraces('slow_keystroke');
  }
  // sampled: this runs per keystroke on a hot path, slow keystrokes always record
  if (slow || Math.random() < SELECT_FILTER_SAMPLE_RATE) {
    selectFilterSeconds.record(durationMs / 1000, { options_count: getCountBucket(optionsCount) });
  }
}

// --- /api/ resource observer: bytes, TTFB, duplicate fetches ---

let duplicateCounts = new Map();

export function resetDuplicateTracking() {
  duplicateCounts = new Map();
}

export function recordResourceEntry(entry) {
  let pathname;
  try {
    pathname = new URL(entry.name, window.location.origin).pathname;
  } catch (e) {
    return;
  }
  // ignore non-API resources and the telemetry pipeline itself
  if (!pathname.startsWith('/api/') || pathname.startsWith('/api/ui/metrics') || pathname.startsWith('/api/ui/otel')) {
    return;
  }
  const labels = { endpoint: getApiEndpointLabel(pathname) };
  if (entry.decodedBodySize > 0) {
    apiDecodedBytesTotal(labels, entry.decodedBodySize);
  }
  if (entry.encodedBodySize > 0) {
    apiEncodedBytesTotal(labels, entry.encodedBodySize);
  }
  if (entry.responseStart > 0) {
    apiTtfbSeconds.record((entry.responseStart - entry.startTime) / 1000, labels);
  }
  // duplicate detection keys on the full URL (query included): identical request repeated
  if (duplicateCounts.size >= DUPLICATE_TRACKING_MAX_URLS && !duplicateCounts.has(entry.name)) {
    resetDuplicateTracking();
  }
  const count = (duplicateCounts.get(entry.name) || 0) + 1;
  duplicateCounts.set(entry.name, count);
  if (count > 1) {
    duplicateFetchTotal(labels, 1);
  }
}

function installResourceObserver() {
  if (typeof PerformanceObserver === 'undefined') {
    return;
  }
  try {
    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach(recordResourceEntry);
    });
    // buffered so boot-time fetches (where the duplicate waste is worst) are included
    observer.observe({ type: 'resource', buffered: true });
  } catch (e) {
    // environments without resource timing support (some test runners)
  }
}

// --- store inventory: resident models per collection (heap-ratchet composition) ---

let inventory = new Map();
let heapPeakBytes = 0;

export function sampleStoreInventory($store) {
  const next = new Map();
  Object.keys($store).forEach((storeName) => {
    const store = $store[storeName];
    if (!store || typeof store !== 'object') {
      return;
    }
    // own enumerable props only — never walk the prototype
    Object.keys(store).forEach((key) => {
      try {
        const candidate = store[key];
        if (
          candidate &&
          typeof candidate === 'object' &&
          'hasFetched' in candidate &&
          typeof candidate.unfilteredSize === 'number' &&
          candidate.unfilteredSize >= STORE_INVENTORY_MIN_MODELS
        ) {
          next.set(`${storeName}.${key}`, {
            value: candidate.unfilteredSize,
            attributes: { store: storeName, collection: key }
          });
        }
      } catch (e) {
        // some store properties are computeds that can throw pre-boot; skip them
      }
    });
  });
  inventory = next;
}

let initialized = false;

export function initializePerfChokepoints({ $store, history } = {}) {
  if (initialized) {
    return;
  }
  initialized = true;

  installResourceObserver();

  createObservableGauge(
    { name: 'dom_node_count', description: 'Live DOM node count, sampled at each metric collection' },
    () => ({ value: document.getElementsByTagName('*').length, attributes: { route: getRouteLabel() } })
  );
  createObservableGauge(
    { name: 'js_heap_used_bytes', description: 'performance.memory.usedJSHeapSize (Chrome only)' },
    () => {
      const memory = window.performance?.memory;
      if (!memory) {
        return undefined;
      }
      heapPeakBytes = Math.max(heapPeakBytes, memory.usedJSHeapSize);
      return memory.usedJSHeapSize;
    }
  );
  createObservableGauge(
    { name: 'js_heap_limit_bytes', description: 'performance.memory.jsHeapSizeLimit (Chrome only)' },
    () => window.performance?.memory?.jsHeapSizeLimit
  );
  createObservableGauge(
    {
      name: 'js_heap_peak_bytes',
      description: 'Peak observed JS heap this session (OOM proxy)',
      // heap-peak gauge is on the company_id allowlist (no route/endpoint dimension)
      includeCid: true
    },
    () => (heapPeakBytes > 0 ? heapPeakBytes : undefined)
  );
  createObservableGauge(
    { name: 'store_resident_models', description: 'Resident models per collection, sampled on route change' },
    () => [...inventory.values()]
  );

  if ($store) {
    // the interesting post-boot state exists only after the boot waves settle
    setTimeout(() => sampleStoreInventory($store), POST_BOOT_INVENTORY_DELAY_MS);
    let lastPathname = window.location.pathname;
    history?.listen((location) => {
      // query-string and hash updates (explorer state, tab params) stay in the same route view
      if (location?.pathname === lastPathname) {
        return;
      }
      lastPathname = location?.pathname;
      resetDuplicateTracking();
      sampleStoreInventory($store);
    });
  }
}
